import React from "react"
import { NoExperiencesModal, DeactivateDeleteModal, RESOURCE_TYPES } from "ui/DeactivateDeleteModals"
import { CREW_DELETE_DEACTIVATE_MODES } from "../../../constants"
import PropTypes from "prop-types"
import { Modal, Spin } from "antd"

/**
 * DeactivateDeleteModals component renders the appropriate modal based on mode for crew members
 * @param {object} modalState - Modal state from useCrewModals hook
 * @param {boolean} activateModalVisible - Activate modal visibility
 * @param {object} selectedCrew - Selected crew for activation
 * @param {Function} onClose - Close modal handler
 * @param {Function} onConfirm - Confirm action handler
 * @param {Function} onActivateConfirm - Activate confirm handler
 * @param {boolean} deactivateLoading - Deactivate action loading state
 * @param {boolean} deleteLoading - Delete action loading state
 * @param {boolean} deactivationDetailsLoading - Deactivation details loading state
 * @param {boolean} deletionDetailsLoading - Deletion details loading state
 * @param {Function} t - Translation function
 */
const DeactivateDeleteModals = ({
  modalState,
  activateModalVisible,
  selectedCrew,
  onClose,
  onConfirm,
  onActivateConfirm,
  deactivateLoading,
  deleteLoading,
  deactivationDetailsLoading,
  deletionDetailsLoading,
  t
}) => {
  // Activate modal - simple confirmation
  if (activateModalVisible && selectedCrew) {
    const crewName = selectedCrew?.name || ""
    return (
      <NoExperiencesModal
        open={activateModalVisible}
        title={t("crewMember.detail.actions.activate")}
        description={t("crewMember.modals.activate_crew_description_prefix")}
        boldText={crewName}
        afterBoldText={t("crewMember.modals.activate_crew_description_suffix")}
        onCancel={onClose}
        onOk={onActivateConfirm}
        okText={t("global.activate")}
        cancelText={t("global.cancel")}
        loading={deactivateLoading || deleteLoading}
      />
    )
  }

  if (!modalState?.visible) return null

  const crewName = modalState?.crew?.name || ""
  const crewRole = modalState?.crew?.role || ""
  const mode = modalState?.mode
  const details = modalState?.details || {}
  const action = modalState?.action
  const expMessage = details.exp_message || null
  const bookingMessage = details.booking_message || null

  const getWarningMessage = () => {
    if (mode === CREW_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE) {
      return null
    }
    if (expMessage) return expMessage
    if (bookingMessage) return bookingMessage
    if (action === "deactivate") {
      return t("crewMember.modals.warning_deactivate_remove", { name: crewName })
    }
    return t("crewMember.modals.warning_delete_remove", { name: crewName })
  }

  const displayName = crewRole ? `${crewName} (${crewRole})` : crewName
  const assignedTripsCount = details.assigned_trips?.length || 0
  const activeBookingsCount = details.active_bookings?.length || 0

  // Generate subtitle based on action and mode
  const getSubtitle = () => {
    if (action === "deactivate") {
      switch (mode) {
        case CREW_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING:
          return (
            <>
              <strong>{crewName}</strong> {t("crewMember.modals.is_assigned_to")} {assignedTripsCount}{" "}
              {t("crewMember.modals.experiences")}:
            </>
          )
        case CREW_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE:
        case CREW_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE:
          return (
            <>
              <strong>{displayName}</strong> {t("crewMember.modals.is_assigned_to")} ({activeBookingsCount}){" "}
              {t("crewMember.modals.upcoming_trips")}. {t("crewMember.modals.to_deactivate")},{" "}
              {t("crewMember.modals.please_reassign_bookings")}.
            </>
          )
        default:
          return (
            <>
              {t("crewMember.modals.deactivate_crew_description_prefix")}
              <strong>{crewName}</strong>
              {t("crewMember.modals.deactivate_crew_description_suffix")}
            </>
          )
      }
    } else if (action === "delete") {
      switch (mode) {
        case CREW_DELETE_DEACTIVATE_MODES.ASSIGNED_EXPERIENCES_NO_ACTIVE_BOOKING:
          return (
            <>
              <strong>{crewName}</strong> {t("crewMember.modals.is_assigned_to")} {assignedTripsCount}{" "}
              {t("crewMember.modals.experiences")}:
            </>
          )
        case CREW_DELETE_DEACTIVATE_MODES.FULLY_SUBSTITUTABLE:
        case CREW_DELETE_DEACTIVATE_MODES.PARTIALLY_SUBSTITUTABLE:
          return (
            <>
              <strong>{displayName}</strong> {t("crewMember.modals.is_assigned_to")} ({activeBookingsCount}){" "}
              {t("crewMember.modals.upcoming_trips")}. {t("crewMember.modals.to_delete")},{" "}
              {t("crewMember.modals.please_reassign_bookings")}.
            </>
          )
        default:
          return (
            <>
              {t("crewMember.modals.delete_crew_description_prefix")}
              <strong>{crewName}</strong>
              {t("crewMember.modals.delete_crew_description_suffix")}
            </>
          )
      }
    }
    return null
  }

  const isNoExperiencesMode = mode === CREW_DELETE_DEACTIVATE_MODES.NO_EXPERIENCES
  const isDeactivateAction = action === "deactivate"
  const actionLoading = isDeactivateAction ? deactivateLoading : deleteLoading
  const detailsLoadingState = isDeactivateAction ? deactivationDetailsLoading : deletionDetailsLoading
  const combinedLoading = actionLoading || detailsLoadingState

  // Render NoExperiences modal for simple cases
  if (isNoExperiencesMode) {
    return (
      <NoExperiencesModal
        open={modalState.visible}
        title={isDeactivateAction ? t("crewMember.modals.deactivate_crew_title") : t("crewMember.modals.delete_crew_title")}
        description={
          isDeactivateAction
            ? t("crewMember.modals.deactivate_crew_description_prefix")
            : t("crewMember.modals.delete_crew_description_prefix")
        }
        boldText={crewName}
        afterBoldText={
          isDeactivateAction
            ? t("crewMember.modals.deactivate_crew_description_suffix")
            : t("crewMember.modals.delete_crew_description_suffix")
        }
        onCancel={onClose}
        onOk={onConfirm}
        okText={isDeactivateAction ? t("global.deactivate") : t("global.delete")}
        cancelText={t("global.cancel")}
        loading={actionLoading}
      />
    )
  }

  // Render DeleteDeactivate modal for complex cases (with mode)
  if (mode) {
    return (
      <DeactivateDeleteModal
        visible={modalState.visible}
        onCancel={onClose}
        onOk={onConfirm}
        title={isDeactivateAction ? t("crewMember.modals.deactivate_crew_title") : t("crewMember.modals.delete_crew_title")}
        subtitle={getSubtitle()}
        okText={isDeactivateAction ? t("global.deactivate") : t("global.delete")}
        cancelText={t("global.cancel")}
        loading={combinedLoading}
        mode={mode}
        list={details.assigned_trips || []}
        upcomingBookings={details.active_bookings || []}
        assignedExperiences={details.assigned_trips || []}
        warning={getWarningMessage()}
        expMessage={expMessage}
        bookingMessage={bookingMessage}
        resourceType={RESOURCE_TYPES.CREW}
        translationPrefix="crewMember"
      />
    )
  }

  // Show loading spinner while fetching details
  if (detailsLoadingState) {
    return (
      <Modal
        open={modalState.visible}
        title={isDeactivateAction ? t("crewMember.modals.deactivate_crew_title") : t("crewMember.modals.delete_crew_title")}
        footer={null}
        onCancel={onClose}
        centered
      >
        <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
          <Spin size="large" />
        </div>
      </Modal>
    )
  }

  return null
}

DeactivateDeleteModals.propTypes = {
  modalState: PropTypes.shape({
    visible: PropTypes.bool,
    mode: PropTypes.oneOf(Object.values(CREW_DELETE_DEACTIVATE_MODES)),
    crew: PropTypes.shape({
      name: PropTypes.string,
      role: PropTypes.string
    }),
    details: PropTypes.shape({
      exp_message: PropTypes.string,
      booking_message: PropTypes.string,
      assigned_trips: PropTypes.array,
      active_bookings: PropTypes.array
    }),
    action: PropTypes.string
  }),
  activateModalVisible: PropTypes.bool,
  selectedCrew: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    role: PropTypes.string
  }),
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onActivateConfirm: PropTypes.func,
  deactivateLoading: PropTypes.bool,
  deleteLoading: PropTypes.bool,
  deactivationDetailsLoading: PropTypes.bool,
  deletionDetailsLoading: PropTypes.bool,
  t: PropTypes.func.isRequired
}

DeactivateDeleteModals.defaultProps = {
  modalState: null,
  activateModalVisible: false,
  selectedCrew: null,
  onActivateConfirm: null,
  deactivateLoading: false,
  deleteLoading: false,
  deactivationDetailsLoading: false,
  deletionDetailsLoading: false
}

export default DeactivateDeleteModals
